/**
 * Loomia PWA entrypoint (P1-T11): app shell with hash routing, auth gate,
 * top bar + bottom tab bar, global toasts and error handling.
 *
 * Route map (see src/router.ts):
 *   #/home #/explore #/collection  → tab screens (authed)
 *   #/login #/auth/verify?token=…   → auth (public)
 *   #/design                        → design-system preview (public)
 *   #/template/:slug #/editor/:tool #/result/:jobId #/paywall #/profile #/settings
 */
import './styles/tokens.css';
import './styles/shell.css';
import './styles/editor.css';
import './styles/result.css';
import './styles/collection.css';
import './styles/paywall.css';
import './styles/checkout.css';
import './styles/explore.css';
import './styles/category.css';
import './styles/i2v.css';
import './styles/changeOutfit.css';
import './styles/pwa.css';
import './styles/home.css';
import './styles/detail.css';
import './styles/compareSlider.css';
import './styles/uploadGuide.css';
import './styles/signin.css';
import './styles/daily.css';
import './styles/create.css';
import './styles/profile.css';
import './styles/settings.css';
import './styles/welcome.css';
import { initInstallPrompt } from './pwa/install.js';
import { api } from './api/client.js';
import { navigate, parseHash, type Route } from './router.js';
import {
  authChanged,
  ensureGuestSession,
  hasAccount,
  initAuth,
  isAuthed,
  setReturnTo,
  takeReturnTo,
} from './state/auth.js';
import { clearBalance, refreshCredits } from './state/credits.js';
import { toast, toastApiError } from './ui/toast.js';
import { renderTopBar } from './components/topbar.js';
import { renderBottomNav } from './components/bottomnav.js';
import { placeholderHtml } from './components/placeholder.js';
import { renderHome } from './pages/home.js';
import { renderExplore } from './pages/explore.js';
import { renderCreate } from './pages/create.js';
import { renderCategory } from './pages/category.js';
import { renderCollection } from './pages/collection.js';
import { renderLogin } from './pages/login.js';
import { renderVerify } from './pages/verify.js';
import { renderSettings } from './pages/settings.js';
import { renderProfile } from './pages/profile.js';
import { renderWelcome, welcomeSeen } from './pages/welcome.js';
import { renderDesign } from './pages/design.js';
import { renderTemplateDetail } from './pages/templateDetail.js';
import { renderEditor } from './pages/editor.js';
import { renderResult } from './pages/result.js';
import { renderPaywall } from './pages/paywall.js';
import { closeDailyReward, maybeAutoOpenDailyReward } from './ui/dailyReward.js';
import { renderCheckout } from './pages/checkout.js';
import { captureAttribution } from './marketing/attribution.js';
import { initPixels } from './marketing/pixels.js';

// The API emails links shaped `/login/verify?token=…` (path form). The SPA is
// hash-routed, so translate to `#/auth/verify?token=…` before the router runs.
if (window.location.pathname === '/login/verify' && window.location.search) {
  window.history.replaceState(null, '', `/#/auth/verify${window.location.search}`);
}

// Guests can browse these without an account; everything else asks for sign-in
// at the moment it is needed (upload / generate / collection / paywall / settings),
// then returns the user to where they were going.
const PUBLIC_ROUTES = new Set(['welcome', 'paywall', 'login', 'verify', 'design', 'home', 'explore', 'create', 'template', 'editor', 'category', 'notfound']);
// Full-screen auth/preview pages without the top bar and tab bar.
const NO_CHROME_ROUTES = new Set(['welcome', 'login', 'verify', 'design', 'paywall', 'checkout', 'template', 'editor', 'category']);
const TAB_ROUTES = new Set(['home', 'explore', 'create', 'collection', 'profile', 'settings']);

let topbarCleanup: (() => void) | null = null;

function shell(): { topbar: HTMLElement; page: HTMLElement; tabbar: HTMLElement } {
  const root = document.getElementById('app');
  if (!root) throw new Error('[web] #app root missing');
  let topbar = document.getElementById('topbar');
  let page = document.getElementById('page');
  let tabbar = document.getElementById('tabbar');
  if (!topbar || !page || !tabbar) {
    root.innerHTML = `
      <div class="shell app-shell">
        <header class="topbar" id="topbar" hidden></header>
        <main class="page" id="page"></main>
        <div class="tabbar" id="tabbar" hidden></div>
      </div>
    `;
    topbar = document.getElementById('topbar')!;
    page = document.getElementById('page')!;
    tabbar = document.getElementById('tabbar')!;
  }
  return { topbar, page, tabbar };
}

function setChrome(topbar: HTMLElement, tabbar: HTMLElement, page: HTMLElement, routeName: string): void {
  const showTopbar = !NO_CHROME_ROUTES.has(routeName);
  const showTabbar = !NO_CHROME_ROUTES.has(routeName);

  topbar.hidden = !showTopbar;
  tabbar.hidden = !showTabbar;
  page.classList.toggle('no-chrome', !showTopbar && !showTabbar);

  if (topbarCleanup) {
    topbarCleanup();
    topbarCleanup = null;
  }
  if (showTopbar) {
    topbarCleanup = renderTopBar(topbar);
  } else {
    topbar.innerHTML = '';
  }
  if (showTabbar) {
    renderBottomNav(tabbar, routeName === 'settings' ? 'profile' : TAB_ROUTES.has(routeName) ? routeName : '');
  } else {
    tabbar.innerHTML = '';
  }
}

function renderRoute(route: Route): void {
  const { topbar, page, tabbar } = shell();

  // Auth gate.
  // Pages that need a real account (guest sessions do not count).
  if (!hasAccount() && !PUBLIC_ROUTES.has(route.name)) {
    setReturnTo(window.location.hash);
    navigate('/login');
    return;
  }
  if (hasAccount() && (route.name === 'login' || route.name === 'verify' || route.name === 'welcome')) {
    navigate(takeReturnTo() ?? '/home');
    return;
  }
  // First launch without an account: show the welcome screen once.
  if (route.name === 'home' && !hasAccount() && !welcomeSeen()) {
    navigate('/welcome');
    return;
  }

  // Uploading a photo needs a session; make an anonymous one in the background
  // so the user is never asked to sign in just to try a tool.
  if (route.name === 'editor' && !isAuthed()) void ensureGuestSession();

  setChrome(topbar, tabbar, page, route.name);
  // Sheets belong to the page that opened them.
  closeDailyReward();
  // Full-screen flows (welcome, paywall, editor…) must not be covered by the PWA install banner.
  document.body.classList.toggle('hide-install', NO_CHROME_ROUTES.has(route.name));

  switch (route.name) {
    case 'welcome':
      renderWelcome(page);
      break;
    case 'home':
      renderHome(page);
      // Retention: offer today's reward once per day when it can be claimed.
      void maybeAutoOpenDailyReward();
      break;
    case 'explore':
      renderExplore(page);
      break;
    case 'create':
      renderCreate(page);
      break;
    case 'collection':
      renderCollection(page);
      break;
    case 'login':
      renderLogin(page);
      break;
    case 'verify':
      renderVerify(page, route.query.get('token'));
      break;
    case 'design':
      // Design preview renders its own .shell; mount it directly.
      page.innerHTML = '';
      renderDesign(page);
      break;
    case 'category':
      renderCategory(page, route.params['slug'] ?? '');
      break;
    case 'template':
      renderTemplateDetail(page, route.params['slug'] ?? '');
      break;
    case 'editor':
      renderEditor(page, route.params['tool'] ?? '', route.query);
      break;
    case 'result':
      renderResult(page, route.params['jobId'] ?? '');
      break;
    case 'paywall':
      renderPaywall(page, route.query);
      break;
    case 'checkout':
      renderCheckout(page, route.params['id'] ?? '');
      break;
    case 'profile':
      renderProfile(page);
      break;
    case 'settings':
      renderSettings(page, route.params['sub'] ?? '');
      break;
    default:
      page.innerHTML = placeholderHtml({
        title: 'Not found',
        taskId: '—',
        copy: 'This page does not exist.',
      });
      break;
  }

  window.scrollTo(0, 0);
}

function route(): void {
  renderRoute(parseHash(window.location.hash));
}

/**
 * The backend emails magic links as `{appBaseUrl}/login/verify?token=…`
 * (path-style). If the PWA is served with SPA fallback, index.html loads and
 * we rewrite to the hash route the router understands.
 */
function rewritePathStyleVerifyLink(): void {
  if (window.location.pathname !== '/login/verify') return;
  const token = new URLSearchParams(window.location.search).get('token');
  const target = token ? `#/auth/verify?token=${encodeURIComponent(token)}` : '#/login';
  window.history.replaceState(null, '', target);
}

function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        // A new SW version installed while the app is open: the worker
        // skip-waits, so tell the user a reload picks up the update.
        reg.addEventListener('updatefound', () => {
          const next = reg.installing;
          if (!next) return;
          next.addEventListener('statechange', () => {
            if (next.state === 'installed' && navigator.serviceWorker.controller) {
              toast('A new version of Loomia is available — reload to update.');
            }
          });
        });
      })
      .catch((err) => {
        console.warn('[web] service worker registration failed:', err);
      });
  });
}

function installGlobalErrorHandling(): void {
  window.addEventListener('error', (e) => {
    console.error('[web] uncaught error:', e.error ?? e.message);
  });
  window.addEventListener('unhandledrejection', (e) => {
    console.error('[web] unhandled rejection:', e.reason);
    toastApiError(e.reason, 'Something went wrong. Please try again.');
  });
}

async function boot(): Promise<void> {
  captureAttribution();
  rewritePathStyleVerifyLink();
  initPixels();
  installGlobalErrorHandling();
  registerServiceWorker();
  initInstallPrompt();

  // Session expired mid-use → drop to login (guard against redirect loops).
  api.onUnauthorized = () => {
    clearBalance();
    authChanged.emit();
    const current = parseHash(window.location.hash).name;
    if (!PUBLIC_ROUTES.has(current)) {
      toast('Session expired. Please sign in again.');
      setReturnTo(window.location.hash);
      navigate('/login');
    }
  };

  const authed = await initAuth();
  if (authed) {
    // Best-effort: a failed fetch just leaves the pill showing "—" (tap retries).
    void refreshCredits().catch(() => undefined);
  }

  // Refresh the balance when the user comes back to the tab.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && isAuthed()) {
      void refreshCredits().catch(() => undefined);
    }
  });

  // Signed in on another tab (magic link) or session changed: refresh the top/tab
  // bars in place. Never re-render the page itself, so a half-finished editor
  // keeps its photo.
  authChanged.on(() => {
    const { topbar, page, tabbar } = shell();
    setChrome(topbar, tabbar, page, parseHash(window.location.hash).name);
  });

  window.addEventListener('hashchange', route);
  route();

}

void boot().catch((err: unknown) => {
  console.error('[web] boot failed:', err);
  const root = document.getElementById('app');
  if (root) {
    root.innerHTML = `
      <div class="shell"><div class="empty" style="padding-top: 96px;">
        <div class="empty-icon">⚠️</div>
        <p class="muted">Loomia failed to start. Please reload the page.</p>
        <button class="btn btn-primary" onclick="location.reload()">Reload</button>
      </div></div>
    `;
  }
});
